// eslint-disable-next-line max-classes-per-file
import { createSelector } from 'reselect';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { Map as ImmutableMap, List as ImmutableList } from 'immutable';
import lodash from 'lodash';

import { getLogger } from 'companion-app-components/utils/core';
import { accountsSelectors } from 'companion-app-components/flux/accounts';
import { categoriesSelectors } from 'companion-app-components/flux/categories';
import { chartOfAccountsSelectors } from 'companion-app-components/flux/chart-of-accounts';
import { transactionsUtils } from 'companion-app-components/flux/transactions';

import { getTransactionsByAccountId, getLoadPending as getTransactionsLoadPending } from 'data/transactions/selectors';
import { getSharedCurrency, isUnacceptedScheduledTxn, isUnAcceptedTxnType } from 'data/transactions/utils';
import { isTxnItemReportableSpending, isTxnItemReportableIncome, isPostedOnDateInterval, getReportableTxn } from 'data/otReports/utils';

const log = getLogger('data/spending/selectors');

const EVERYTHING_ELSE_LEGEND = 'Everything Else';

/**
 * The COA ID corresponding to "uncategorized".
 */
export const UNCATEGORIZED_ID = '0';
export const OTHER_ID = 'OTHER';

/**
 *
 * UTILS =============================================================================
 *
 */

/**
 * Is the passed-in COA node referenced by coaNodeId a child of
 * the provided parentCoaNodeId?
 *
 * @param {} coaNodesById
 * @param {*} parentCoaNodeId
 * @param {*} coaNodeId
 */
export function isDescendent(coaNodesById, parentCoaNodeId, coaNodeId) {
  // uncategorized is logically a top-level category with no children, so only
  // show uncategorized txns if either the top-level cats *or* uncategorized
  // are selected
  if (coaNodeId === UNCATEGORIZED_ID) {
    return parentCoaNodeId === null || parentCoaNodeId === UNCATEGORIZED_ID;
  }
  const childNode = coaNodesById.get(coaNodeId);
  if (!childNode) {
    log.warn('no child node for id', coaNodesById, coaNodeId);
    return false;
  }
  if (parentCoaNodeId) {
    return childNode.path.contains(parentCoaNodeId) || childNode.id === parentCoaNodeId;
  }
  // parent node is null, so return everything
  return true;
}

/**
 * Get the COA ID that is closest to the provided parent, i.e., the immediate
 * child, or, if the split is actually associated directly with the parent,
 * the parent ID. If the provided parentCoaNodeId is null, return the top-level
 * COA ID associated with the split. If the parentCoaNodeId is not null, but the
 * split does not have that ID in its path, return null.
 *
 * @param {*} coaNodesById map from id of coa node to node object for lookup
 * up categories by id
 * @param {*} split the transaction or split to inspect
 */
export function getClosestChildCoaId(coaNodesById, parentCoaNodeId, split) {
  // if the split is uncategorized, return the known id for uncategorized
  if (!split.coa) return null;

  if (split.coa.type === 'UNCATEGORIZED') {
    return UNCATEGORIZED_ID;
  }
  // if the split is directly in the parent category, return the parent category
  if (split.coa.id === parentCoaNodeId) {
    return parentCoaNodeId;
  }
  // otherwise we need to look up the node
  const coaNode = coaNodesById.get(split.coa.id);
  if (!coaNode) {
    // hmmm - this probably should never happen if the data is consistent
    return null;
  }
  // if the parent is null, we want to simply return everything by its topmost category
  if (parentCoaNodeId === null) {
    return (coaNode.path && coaNode.path.get(0)) || coaNode.id;
  }
  // otherwise, look up where the parent lies in the coa path
  // note that coaNode.path is an immutablejs list, not an array
  const idx = coaNode.path.indexOf(parentCoaNodeId);
  // this shouldn't happen if the splits were previously filtered
  if (idx === -1) return null;
  // return the first child down from the parent if one exists in the path
  if (idx + 1 < coaNode.path.size) {
    return coaNode.path.get(idx + 1);
  }
  // otherwise the first child down *is* the node, so return that
  return coaNode.id;
}

/**
 * Returns a predicate function that tests splits to see if they
 * are in a COA that is a descendent of the specified parent COA.
 */
export function isConsideredSplit(coaNodesById, filterCriteria) {
  return (split) => {
    if (split.coa) {
      switch (split.coa.type) {
        case 'CATEGORY':
        case 'UNCATEGORIZED':
          if (filterCriteria && filterCriteria.topLevelOnly) {
            return split.coa.id === filterCriteria.parentCoaNodeId;
          }
          // to be considered, the split must be a descendent of the parent category (if one is specified),
          // and, if a filtering node set is specified, the closest (to the parent) child must be a member of that set
          return isDescendent(coaNodesById, filterCriteria && filterCriteria.parentCoaNodeId, split.coa.id) &&
            (!filterCriteria || !filterCriteria.coaNodeSet || !filterCriteria.coaNodeSet.has ||
              filterCriteria.coaNodeSet.has(getClosestChildCoaId(coaNodesById, filterCriteria.parentCoaNodeId, split)));
        default:
          return false;
      }
    }
    return false;
  };
}

/**
 * Get the name to use for a COA/category.
 *
 * @param {*} coaNodesById
 * @param {*} coaId
 */
function getCoaName(coaNodesById, coaId) {
  if (coaId === null) {
    return 'All Categories';
  }
  if (coaId === UNCATEGORIZED_ID) {
    return 'Uncategorized';
  }
  const node = coaNodesById.get(coaId);
  if (node && node.name) {
    return node.name;
  }
  return 'Unknown';
}

/**
 * Get the full name (with path) to use for a COA/category.
 *
 * @param {*} coaNodesById
 * @param {*} coaId
 */
export function getCoaPathName(coaNodesById, coaId) {
  if (coaId === null) {
    return 'All Categories';
  }
  if (coaId === UNCATEGORIZED_ID) {
    return 'Uncategorized';
  }
  const node = coaNodesById.get(coaId);
  if (node && node.fullPathName) {
    return node.fullPathName;
  }
  return 'Unknown';
}

/**
 * Take an array of transaction arrays, and return a deduped immutable list
 * containing all the transactions.
 *
 * @param {} txnArrays
 */
export function mergeTransactions(txnArrays) {
  const merged = txnArrays.reduce((accum, val) => accum.concat(val), []);
  const idSet = new Set();
  // this is so clumsy, would be better in a better language...
  // I take offense to that!
  for (let i = 0; i < merged.length;) {
    const { id } = merged[i];
    if (idSet.has(id)) {
      merged.splice(i, 1);
    } else {
      idSet.add(id);
      i += 1;
    }
  }
  return ImmutableList(merged);
}

/**
 * Get all the account IDs as an immutable list.
 */
export const getAccountIds = createSelector(
  accountsSelectors.getReportableAccountsById,
  (accountsById) => ImmutableList(accountsById.keys()),
);

export function createDummyDisplayData(title, dataKey = 'byCategory') {
  return {
    [dataKey]: [{ x: title, y: 1, total: 0, txns: ImmutableList(), coaNodeId: UNCATEGORIZED_ID }],
    allTransactions: ImmutableList(),
    total: 0,
  };
}

export function createDummyDisplayDataForSpendingByPayee() {
  return {
    byPayee: [],
    allTransactions: ImmutableList(),
    total: 0,
  };
}

/**
 * Get the category name associated with the selected category.
 */
export const getCategoryName = createSelector(
  chartOfAccountsSelectors.getChartOfAccountNodesById,
  (_, props) => props.categoryId,
  (coaNodesById, categoryId) => getCoaName(coaNodesById, categoryId),
);

const includeTxnByCategoryType = (isIncome, txn, categoriesById) => {
  const isIncomeTransaction = isTxnItemReportableIncome(txn, categoriesById);
  const isExpenseTransaction = isTxnItemReportableSpending(txn, categoriesById);

  if (isIncome) {
    return isIncomeTransaction;
  }

  return isExpenseTransaction;
};

export const filterTxnByCategory = (txn, filterCriteria, coaNodesById) => isConsideredSplit(coaNodesById, filterCriteria)(txn);

export const filterTxnByTag = (txn, filterCriteria) => {
  if (filterCriteria.id === null) {
    return true;
  }

  if (filterCriteria.id === 'Untagged' && !txn.tags) {
    return true;
  }

  if (filterCriteria.nodeSet) {
    const tags = txn.tags || [];
    return tags.find((tag) => filterCriteria.nodeSet.has(tag.id));
  }

  if (txn.split && txn.split.items && txn.split.items.size) {
    let found = false;
    txn.split.items.forEach((item) => {
      if (item.tags && item.tags.size) {
        item.tags.forEach((tag) => {
          if (tag.id === filterCriteria.id) {
            found = true;
          }
        });
      }
    });
    if (found) {
      return true;
    }
  }

  return txn.tags && txn.tags.find((tag) => tag.id === filterCriteria.id);
};

export const filterTxnByPayee = (txn, filterCriteria) => {

  if (filterCriteria.id === null) {
    return true;
  }

  if (filterCriteria.nodeSet) {
    return filterCriteria.nodeSet.has(txn.payee);
  }

  if (!txn.payee) {
    return false;
  }
  return txn.payee?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '') === filterCriteria.id?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
};

const filters = {
  category: filterTxnByCategory,
  tag: filterTxnByTag,
  payee: filterTxnByPayee,
};

// This is re-used in all selectors (by payee, by tag and by category)
// It returns all txns filtered by the different requirements and also returns their aggregated total
export const getTransactionsWithTotals = createCachedSelector(
  (state) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  (_, props) => props.accountIds,
  accountsSelectors.getReportableAccountsById,
  getTransactionsByAccountId,
  chartOfAccountsSelectors.getChartOfAccountNodesById,
  categoriesSelectors.getCategoriesById,
  (_, props) => props.dateInterval,
  (_, props) => props.filterCriteria,
  (_, props) => props.isIncome,
  (_, props) => props.dateRange,
  (isLoadPending, accountIds, accountsById, transactionsByAccountId, coaNodesById,
    categoriesById, dateInterval, filterCriteria, isIncome) => {

    if (isLoadPending || coaNodesById.isEmpty()) {
      return {
        txns: [],
        total: 0,
      };
    }

    const isReportableTxn = (txn, realTxn) => {
      // We only want to return either Income or Expense txns based on what we're asking
      const test1 = includeTxnByCategoryType(isIncome, txn, categoriesById);
      // We don't want scheduled txns in month to date or year to date
      const test2 = !isUnAcceptedTxnType(txn);
      const test3 = filters[filterCriteria.type](filterCriteria.type === 'category' ? txn : realTxn, filterCriteria, coaNodesById);
      return test1 && test2 && test3;
    };

    let accountIdsToUse = accountIds || accountsById.keySeq();
    accountIdsToUse = accountIdsToUse.filter((id) => {
      const account = accountsById.get(id);
      return account !== null && ['BANK', 'CREDIT', 'CASH'].includes(account.type);
    });

    const filteredTxns = [];
    let total = 0;

    accountIdsToUse.forEach((accountId) => {
      const txns = transactionsByAccountId.get(accountId);

      (txns || []).forEach((txn) => {

        if (isPostedOnDateInterval(txn, dateInterval)) {

          const { amount, isReportable } = getReportableTxn(
            txn,
            categoriesById,
            filterCriteria,
            null,
            null,
            (txnItem) => isReportableTxn(txnItem, txn),
            (txnItem) => !txnItem.isExcludedFromReports,
          );

          if (!isUnAcceptedTxnType(txn)) {
            total += amount;
          }

          if (isReportable && !isUnAcceptedTxnType(txn)) {
            filteredTxns.push(txn);
          }
        }
      });
    });

    return {
      txns: filteredTxns,
      total,
    };
  },
)({
  // this line sets the key to use for the cache
  keySelector: (_, props) => {
    const { dateInterval, dateRange, filterCriteria, isIncome } = props;
    const key = `${filterCriteria.type}_${isIncome ? 'income' : 'expense'}_txns_${filterCriteria.name}_${dateInterval.toString()}_${dateRange}`;
    return key;
  },
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

/**
 *
 * BY CATEGORY =============================================================================
 *
 */

/**
 * A CategoryFilterCriteria represents the input object to the selector that filters
 * the categories to be returned. Filtering can be performed by simply specifying
 * a parent node (in which case the children are returned) or both a parent node
 * and a node set (in which case only those specific children are returned).
 *
 * A CategoryFilterCriteria is also an output object in that each data object returned
 * for a category includes a CategoryFilterCriteria that can be used as input to return
 * the designated *children* of that node.
 */
export class CategoryFilterCriteria {
  constructor(parentCoaNodeId, coaNodeSet, name, color, topLevelOnly) {
    // the id of the parent node, or null to indicate a (logical but nonexistent)
    // root node selecting all the top-level categories as children
    this.parentCoaNodeId = parentCoaNodeId;
    // an optional set of children to include, or null to include all
    this.coaNodeSet = coaNodeSet;
    this.name = name;
    this.color = color;
    this.type = 'category';
    this.topLevelOnly = topLevelOnly; // means: exact category, no subcategories
  }

  equals = (other) => (
    this.parentCoaNodeId === other.parentCoaNodeId
      && this.name === other.name
      && lodash.isEqual(this.coaNodeSet, other.coaNodeSet)
  );
}

export const ROOT_CRITERIA = new CategoryFilterCriteria(null, null, 'All Categories');

/**
 * Calculate a map from category id to total spending for that
 * category across all accounts or some subset of accounts if
 * an accounts argument is provided.
 *
 * Note that categories with net spend will have a negative value
 * and categories with net income will have a positive value.
 * Depending on the display requirement, the caller may want
 * to filter out one or the other.
 */
export const getReportByCategory = createSelector(
  (state) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  chartOfAccountsSelectors.getChartOfAccountNodesById,
  getTransactionsWithTotals,
  (_, props) => props.filterCriteria,
  (_, props) => props.isIncome,
  (_, props) => props.reportsFilter,
  (isLoadPending, coaNodesById, txnWithTotals, filterCriteria, isIncome, reportsFilter) => {
    const {
      txns,
    } = txnWithTotals;

    if (isLoadPending) return ImmutableMap();

    const result = ImmutableMap().withMutations((map) => {
      txns.forEach((txn) => {

        const splits = transactionsUtils.isSplitTxn(txn) ? txn.split.items : [txn];

        splits.filter(isConsideredSplit(coaNodesById, filterCriteria)).forEach((split) => {
          const coaIdKey = getClosestChildCoaId(coaNodesById, filterCriteria && filterCriteria.parentCoaNodeId, split);
          const coaName = getCoaName(coaNodesById, coaIdKey);
          const coaPathName = getCoaPathName(coaNodesById, coaIdKey);
          if (coaIdKey !== null && !(isIncome && coaName === 'Uncategorized')) {
            if (!map.has(coaIdKey)) {
              map.set(coaIdKey, {
                amount: 0,
                txns: [],
                coaName,
                coaPathName,
                // note that the full path name is used as the filter criteria name, useful for titling the display
                filterCriteria: new CategoryFilterCriteria(coaIdKey, null, coaPathName, null, filterCriteria.topLevelOnly),
              });
            }
            const data = map.get(coaIdKey);
            // these can and should be strings, ensure they are numbers (TV)

            if (!txn.isExcludedFromReports && (!reportsFilter || reportsFilter(txn))) { // txnFilter returns false if excluded
              if (data) {
                data.amount += Number(split.amount);// + Number(split.amount);
              } else {
                log.error(`either data or split is undefined. data: ${data}, split: ${split}, map: ${map}`);
              }
            }
            data.txns.push(txn);
          }
        });
      });
    });

    return result;
  },
);

/**
 * Create a more heavily-processed version of spending data that is suitable for
 * use as input to one of the Victory widgets.
 */
const getReportByCategoryForDisplay = createSelector(
  (state) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  getReportByCategory,
  accountsSelectors.getReportableAccountsById,
  categoriesSelectors.getCategoriesById,
  getTransactionsWithTotals,
  (_, props) => props.smallestValueToShow,
  (_, props) => props.maxCategories,
  (_, props) => props.filterCriteria,
  (_, props) => props.isIncome,
  (_, props) => props.webFirstCatUncategorize,
  (_, props) => props.theme,
  (isLoadPending, spending, accountsById, categoriesById, txnWithTotals,
    smallestValueToShow, maxCategories, filterCriteria, isIncome, webFirstCatUncategorize, theme) => {
    // if transactions are still loading, return a dummy data set with a single category
    if (isLoadPending) {
      return createDummyDisplayData('Loading...');
    }
    const { total, txns } = txnWithTotals;

    const colorsFromTheme = (((theme || {}).palette || {}).data || {}).sequence1 || [];

    const uncategorized = spending.get('0');

    // transform map to an array of entries for easier manipulation
    let dataByCategory = Array.from(spending.entries());


    // exclude non-expense categories (but preserve uncategorized)
    const categoryType = isIncome ? 'INCOME' : 'EXPENSE';
    dataByCategory = dataByCategory.filter((v) => !categoriesById.has(v[0]) || categoriesById.get(v[0]).type === categoryType);

    // exclude categories with positive totals (net income)
    dataByCategory = dataByCategory.filter((v) => isIncome ? v[1].amount >= 0 : v[1].amount <= 0);
    // compute the sum total of the surviving categories

    // sort descending by total
    dataByCategory.sort((a, b) => isIncome ? b[1].amount - a[1].amount : a[1].amount - b[1].amount);
    // lump the tail categories (over maxCategories) into one "other" category
    if (dataByCategory.length > maxCategories) {
      const firstSummarizedCatPos = maxCategories - 1;

      const otherSum = dataByCategory.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr + value[1].amount, 0);
      const otherTxns = dataByCategory.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr.concat(value[1].txns), []);
      const filterNodeSet = dataByCategory.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr.add(value[0]), new Set());
      dataByCategory = dataByCategory.slice(0, firstSummarizedCatPos);
      dataByCategory.push([OTHER_ID, {
        amount: otherSum,
        txns: otherTxns,
        coaName: 'Other',
        filterCriteria: new CategoryFilterCriteria(filterCriteria.parentCoaNodeId, filterNodeSet, 'Other'),
      }]);
    }
    // if after all that there's nothing left, return a dummy data set
    if (dataByCategory.length === 0) {
      return createDummyDisplayData('No spending to display');
    }

    // create an array of objects suitable to feed to Victory charts
    const displayData = dataByCategory.map(([_, val], index) => {
      const txnsByCategory = ImmutableList.of(val.txns);
      return (
        {
          x: val.coaName,
          y: val.amount / total,
          total: val.amount,
          coaPathName: val.coaPathName,
          txnsByCategory,
          currency: getSharedCurrency(txnsByCategory, accountsById),
          filterCriteria: val.filterCriteria,
          color: filterCriteria.coaNodeSet || filterCriteria.parentCoaNodeId ? colorsFromTheme[index]
            : filterCriteria.color || colorsFromTheme[index],
        });
    });

    const uncategorizedTxns = spending.get('0') || undefined;
    if (webFirstCatUncategorize && uncategorizedTxns) {
      uncategorizedTxns.txns = uncategorized.txns.filter((txn) => !isUnacceptedScheduledTxn(txn));
    }
    return {
      byCategory: displayData,
      allTransactions: ImmutableList(txns),
      total,
      uncategorizedTxns,
    };
  },
);

export const getSpendingByCategoryForDisplay = createSelector(
  (state, props) => getReportByCategoryForDisplay(state,
    {
      ...props,
      isIncome: false,
    }),
  (categorySpendingReport) => categorySpendingReport,
);

export const getIncomeByCategoryForDisplay = createSelector(
  (state, props) => getReportByCategoryForDisplay(state,
    {
      ...props,
      isIncome: true,
    }),
  (categoryIncomeReport) => categoryIncomeReport,
);

/**
 *
 * BY PAYEE =============================================================================
 *
 */

export class PayeeFilterCriteria {
  constructor(payee, nodeSet, name, color) {

    this.id = payee;
    this.nodeSet = nodeSet;
    this.name = name;
    this.color = color;
    this.type = 'payee';
  }

  equals = (other) => (
    this.id === other.id
    && this.name === other.name
    && lodash.isEqual(this.nodeSet, other.nodeSet)
  );
}

export const PAYEE_ROOT_CRITERIA = new PayeeFilterCriteria(null, null, 'All Payees');


/**
 * Function used in the selector below to filter payees by the selected payee from filterCriteria
 */

function filterByPayee(filterCriteria) {
  return (payee) => {
    if ((!filterCriteria.id) || (filterCriteria.nodeSet !== null && filterCriteria.nodeSet.has(payee[0]))) {
      return true;
    }
    const normalizedPayee = payee[0]?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
    const normalizedFilter = filterCriteria.id?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
    return normalizedPayee === normalizedFilter;
  };
}

const getReportByPayeeForDisplay = createSelector(
  (state) => accountsSelectors.getLoadPending(state) || categoriesSelectors.getLoadPending(state) || getTransactionsLoadPending(state),
  accountsSelectors.getReportableAccountsById,
  chartOfAccountsSelectors.getChartOfAccountNodesById,
  getTransactionsWithTotals,
  (_, props) => props.filterCriteria,
  (_, props) => props.isIncome,
  (_, props) => props.reportsFilter,
  (_, props) => props.payeeLimit || 8,
  (_, props) => props.theme,
  (isLoadPending, accountsById, coaNodesById, txnWithTotals, filterCriteria, isIncome, reportsFilter, payeeLimit, theme) => {

    if (isLoadPending) {
      return createDummyDisplayData('Loading...', 'byPayee');
    }
    const { total, txns } = txnWithTotals;

    // if chart-of-accounts hasn't completely loaded this can happen and there will
    // be no data to return, effectively
    if (coaNodesById.isEmpty()) {
      // return ImmutableMap();
      return createDummyDisplayData('Loading...', 'byPayee');
    }

    const colorsFromTheme = (((theme || {}).palette || {}).data || {}).sequence1 || [];

    const normalizedKeys = [];  // used only to store the normalized keys in so that we can check if a normalized key has been set yet
    const result = ImmutableMap().withMutations((map) => {

      txns.forEach((txn) => {
        const key = txn.get('payee');// isSplit ? txn.get('payee') : split.get('payee');
        if (key) {  // could be blank if user entered blank payee
          let keyNormalized = key?.toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
          // make sure we're not finding methods of Array (like "shift", for example),
          // when we check to see if Map has the normalized key.
          if (typeof normalizedKeys[keyNormalized] !== 'string') {
            keyNormalized = `${keyNormalized} `; // add a space to the end
          }
          if (!normalizedKeys[keyNormalized]) {
            normalizedKeys[keyNormalized] = key;
            map.set(key, {
              amount: 0,
              txns: [],
              currency: '',
              filterCriteria: new PayeeFilterCriteria(key, null, key),
            });
          }
          const data = map.get(normalizedKeys[keyNormalized]);
          // these can and should be strings, ensure they are numbers (TV)
          if (!txn.isExcludedFromReports && (!reportsFilter || reportsFilter(txn))) {
            if (data) {
              data.amount += Number(txn.amount);
            } else {
              log.error(`either data or split is undefined.  data: ${data},
                split: ${txn}, map: ${map}, normalizedKeys: ${normalizedKeys},
                keyNormalized: ${keyNormalized}`);
            }
          }
          data.txns.push(txn);
          data.currency = getSharedCurrency([data.txns], accountsById);
        }
      });

    });

    // transform map to an array of entries for easier manipulation
    let dataByPayee = Array.from(result.entries());
    // exclude payees with positive totals
    dataByPayee = dataByPayee.filter(filterByPayee(filterCriteria, isIncome));
    // sort descending by amount
    dataByPayee = dataByPayee.sort((a, b) => isIncome ? b[1].amount - a[1].amount : a[1].amount - b[1].amount);

    if (dataByPayee.length > payeeLimit) {
      const firstSummarizedCatPos = payeeLimit;

      const otherSum = dataByPayee.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr + value[1].amount, 0);
      const otherTxns = dataByPayee.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr.concat(value[1].txns), []);
      const filterNodeSet = dataByPayee.slice(firstSummarizedCatPos).reduce((aggr, value) => aggr.add(value[0]), new Set());
      dataByPayee = dataByPayee.slice(0, firstSummarizedCatPos);
      dataByPayee.push([EVERYTHING_ELSE_LEGEND, {
        amount: otherSum,
        txns: otherTxns,
        name: EVERYTHING_ELSE_LEGEND,
        filterCriteria: new PayeeFilterCriteria(EVERYTHING_ELSE_LEGEND, filterNodeSet, EVERYTHING_ELSE_LEGEND),
      }]);
    }

    // if after all that there's nothing left, return a dummy data set
    if (dataByPayee.length === 0) {
      return createDummyDisplayDataForSpendingByPayee();
    }

    // create an array of objects suitable to feed to Victory charts
    const displayData = dataByPayee.map(([payee, val], index) => {
      const txnsByPayee = mergeTransactions(val.txns); // otherwise we'd have duplicate split transactions
      return (
        {
          //
          x: payee,
          y: Number(val.amount) / total,
          total: val.amount,
          txnsByPayee,
          currency: val.currency,
          filterCriteria: val.filterCriteria,
          excludeFromGraph: val.excludeFromGraph,
          color: filterCriteria.nodeSet
            ? colorsFromTheme[index]
            : filterCriteria.color || colorsFromTheme[index],
        }
      );
    });

    return {
      byPayee: displayData || [],
      allTransactions: ImmutableList(txns),
      total,
    };
  },
);

export const getSpendingByPayeeForDisplay = createSelector(
  (state, props) => getReportByPayeeForDisplay(state,
    {
      ...props,
      isIncome: false,
    }),
  (payeeSpendingReport) => payeeSpendingReport,
);

export const getIncomeByPayeeForDisplay = createSelector(
  (state, props) => getReportByPayeeForDisplay(state,
    {
      ...props,
      isIncome: true,
    }),
  (payeeIncomeReport) => payeeIncomeReport,
);
